import type { FunctionComponent } from 'react';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useAuth } from 'react-oidc-context';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { MethodType } from '@endeavour/payment-integration';

import type { IWallet } from '../../../account/store/account.store';
import { selectWallets } from '../../../account/store/account.store';
import { ContextNavigate } from '../../../shared/components/context-navigate/context-navigate';
import { Loader } from '../../../shared/components/loader/loader';
import { Cashier } from '../../components/cashier/cashier';
import { Error } from '../../components/error/error';
import { Receipt } from '../../components/receipt/receipt';
import { useTransaction } from '../../hooks/use-transaction';

export interface IResponsePageProps {
    methodType: MethodType;
}

declare global {
    interface Window {
        gtag: (key: string, ...args: any[]) => void;
    }
}

declare global {
    interface Window {
        rdt: (key: string, ...args: any[]) => void;
    }
}

declare global {
    interface Window {
        fbq: (key: string, ...args: any[]) => void;
    }
}

export const ResponsePage: FunctionComponent<IResponsePageProps> = ({ methodType }) => {
    const { transactionId } = useParams();
    const intl = useIntl();
    const auth = useAuth();
    const wallets = useSelector(selectWallets);
    const mainWallet = wallets?.filter((wallet: IWallet) => wallet.type === 'main');
    const userProfileId = auth?.user?.profile?.user_profile_id as string;

    const { data, fetching, error } = useTransaction({
        userProfileId,
        transactionId,
    });

    useEffect(() => {
        if (methodType === MethodType.DEPOSIT && data?.walletTransaction?.amount) {
            window.gtag('event', 'deposit', {
                transaction_id: transactionId ?? '',
                transaction_amount: data?.walletTransaction?.amount,
                transaction_currency: data?.walletTransaction?.currencyCode ?? '',
            });
            // window.gtag('event', 'conversion', {
            //     send_to: 'AW-11353277118/ZDllCInOx-YYEL6F1aUq',
            //     transaction_id: transactionId ?? '',
            //     transaction_amount: data?.walletTransaction?.amount,
            //     transaction_currency: data?.walletTransaction?.currencyCode ?? '',
            // });
            window.rdt('track', 'Purchase', {
                currency: data?.walletTransaction?.currencyCode ?? '',
                transactionId: transactionId ?? '',
                value: data?.walletTransaction?.amount,
            });
            window.fbq('track', 'Purchase', {
                currency: data?.walletTransaction?.currencyCode ?? '',
                value: data?.walletTransaction?.amount,
            });
        }
    }, [data]);
    //use data as trigger, if empty data sent it back
    useEffect(() => {
        if (fetching || !data) {
            return;
        }
        if (
            methodType === MethodType.DEPOSIT &&
            data?.walletTransaction?.amount &&
            mainWallet &&
            (mainWallet[0]?.aggregates?.['monetary-credit']?.counter === 1 ||
                mainWallet[0]?.aggregates?.['monetary-credit']?.Counter === 1)
        ) {
            console.log(
                '__________FIRST_DEPOSIT__________c',
                mainWallet[0]?.aggregates?.['monetary-credit']?.counter,
            );
            console.log(
                '__________FIRST_DEPOSIT__________C',
                mainWallet[0]?.aggregates?.['monetary-credit']?.Counter,
            );
            window.gtag('event', 'firstTimeDeposit', {
                transaction_id: transactionId ?? '',
                transaction_amount: data?.walletTransaction?.amount ?? '',
                transaction_currency: data?.walletTransaction?.currencyCode ?? '',
            });
            window.rdt('track', 'firstDeposit', {
                currency: data?.walletTransaction?.currencyCode ?? '',
                transactionId: transactionId ?? '',
                value: data?.walletTransaction?.amount,
            });
            window.fbq('trackCustom', 'firstDeposit', {
                currency: data?.walletTransaction?.currencyCode ?? '',
                value: data?.walletTransaction?.amount,
            });
        }
    }, [mainWallet, data]);

    if (fetching) {
        return <Loader />;
    }

    if (error && transactionId) {
        return (
            <Cashier
                title={intl.formatMessage({
                    id: 'cashier.receipt.error.title',
                    defaultMessage: 'Error',
                })}
            >
                <Error transactionId={transactionId} error={'TRANSACTION_REQUEST_ERROR'} />
            </Cashier>
        );
    }

    if (!data?.walletTransaction) {
        return <ContextNavigate to={'/404'} />;
    }

    const transaction = data.walletTransaction;

    const titleTranslated =
        methodType === MethodType.DEPOSIT
            ? intl.formatMessage({
                  id: `cashier.receipt.deposit-${transaction.status.toLocaleLowerCase()}`,
                  defaultMessage: `Deposit ${transaction.status.toLocaleLowerCase()}`,
              })
            : intl.formatMessage({
                  id: `cashier.receipt.withdrawal-${transaction.status.toLocaleLowerCase()}`,
                  defaultMessage: `Withdrawal ${transaction.status.toLocaleLowerCase()}`,
              });

    const currency = transaction.currencyCode;
    const feeCurrency = transaction.additionalProperties?.FeeCurrency ?? transaction.currencyCode;

    return (
        <Cashier title={titleTranslated}>
            <Receipt
                timestamp={Date.now()}
                transactionId={transactionId ? transactionId : ''}
                transactionAmount={transaction?.amount ?? 0}
                currency={currency}
                feeCurrency={feeCurrency}
                fee={transaction.additionalProperties?.FeeAmount ?? 0}
                afterTransactionBalanceAmount={transaction.balanceAmount}
                transactionStatus={transaction.status}
                method={methodType}
                operationType={transaction.operationType ?? ''}
                error={transaction.errorCode ?? ''}
            />
        </Cashier>
    );
};
